export type ChannelStatusType = 'publish' | 'product'
export type ChannelStatusTone = 'success' | 'error' | 'processing' | 'muted'

export interface ChannelStatusItem {
  channel: 'google' | 'xsolla'
  label: 'GP' | 'XS'
  tooltip: string
  text: string
  tone: ChannelStatusTone
}

type RawChannelState = goodsSetting.ChannelStateValue | goodsSetting.ChannelState

const isStateObject = (value: RawChannelState | undefined): value is goodsSetting.ChannelState =>
  !!value && typeof value === 'object' && 'status' in value

const readState = (value: RawChannelState | undefined) => ({
  status: isStateObject(value) ? value.status : value,
  message: isStateObject(value) ? value.message : undefined,
})

const matchesStatus = (candidates: unknown[], value: unknown) => candidates.includes(value)

const normalizeStatus = (
  rawValue: RawChannelState | undefined,
  type: ChannelStatusType,
  channelScoped: boolean,
): Pick<ChannelStatusItem, 'text' | 'tone'> & { message?: string } => {
  const { status, message } = readState(rawValue)
  const value = typeof status === 'string' ? status.toLowerCase() : status

  if (type === 'publish') {
    if (matchesStatus([1, 10, 'success', 'succeed', 'successful'], value)) {
      return { text: '成功', tone: 'success', message }
    }
    if (matchesStatus([2, 20, -1, 'failed', 'fail', 'error'], value)) {
      return { text: '失败', tone: 'error', message }
    }
    if (matchesStatus([30, 'syncing', 'processing', 'pending', 'running', 'in_progress'], value)) {
      return { text: '同步中', tone: 'processing', message }
    }
    return { text: '--', tone: 'muted', message }
  }

  if (matchesStatus([1, 10, true, 'active', 'enabled', 'valid'], value)) {
    return { text: '有效', tone: 'success', message }
  }
  if (matchesStatus([2, 20, false, 'inactive', 'disabled', 'invalid'], value) || (channelScoped && value === 0)) {
    return { text: '失效', tone: 'muted', message }
  }
  return { text: '--', tone: 'muted', message }
}

const getChannelPrices = (record: goodsSetting.tableDataItem): goodsSetting.ChannelPrices | undefined => {
  const raw = record.channelPrices
  if (!raw) return undefined
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

const getChannelMap = (
  record: goodsSetting.tableDataItem,
  type: ChannelStatusType,
): goodsSetting.ProductChannelStates | undefined => {
  const raw = type === 'publish' ? record.syncStatus : record.prodStatus
  return raw && typeof raw === 'object' ? raw : undefined
}

const getGoogleState = (
  record: goodsSetting.tableDataItem,
  type: ChannelStatusType,
): { value: RawChannelState | undefined; channelScoped: boolean } => {
  const channelMap = getChannelMap(record, type)
  const channelValue = channelMap?.google ?? channelMap?.googlePlay
  if (channelValue !== undefined) return { value: channelValue, channelScoped: true }
  const scalarValue = type === 'publish' ? record.publishStatus : record.prodStatus
  return { value: typeof scalarValue === 'object' ? undefined : scalarValue, channelScoped: false }
}

const getXsollaState = (
  record: goodsSetting.tableDataItem,
  type: ChannelStatusType,
): { value: RawChannelState | undefined; channelScoped: boolean } => {
  const channelMap = getChannelMap(record, type)
  // 只有拿到按渠道拆开的对象值才算渠道级；回退到 xsolla_pay 里的标量时按整体处理（与 GP 一致）
  if (channelMap?.xsolla !== undefined) return { value: channelMap.xsolla, channelScoped: true }
  const xsollaPay = getChannelPrices(record)?.priceInfo?.xsolla_pay
  const scalarValue = type === 'publish' ? xsollaPay?.publishStatus : xsollaPay?.prodStatus
  return { value: scalarValue, channelScoped: false }
}

const getXsollaEnabled = (record: goodsSetting.tableDataItem): boolean | undefined =>
  getChannelPrices(record)?.priceInfo?.xsolla_pay?.enableThirdPay

export const normalizeChannelStatus = (
  record: goodsSetting.tableDataItem,
  type: ChannelStatusType,
): ChannelStatusItem[] => {
  const googleState = getGoogleState(record, type)
  const xsollaState = getXsollaState(record, type)
  const items: ChannelStatusItem[] = [
    {
      channel: 'google',
      label: 'GP',
      tooltip: 'Google 后台状态',
      ...normalizeStatus(googleState.value, type, googleState.channelScoped),
    },
  ]

  const xsollaEnabled = getXsollaEnabled(record)
  if (xsollaEnabled !== false && (xsollaState.value !== undefined || xsollaEnabled === true)) {
    items.push({
      channel: 'xsolla',
      label: 'XS',
      tooltip: 'Xsolla 后台状态',
      ...normalizeStatus(xsollaState.value, type, xsollaState.channelScoped),
    })
  }
  return items
}

export const getOverallProductStatus = (record: goodsSetting.tableDataItem): number => {
  if (typeof record.prodStatus === 'number') return record.prodStatus
  if (typeof record.overallProdStatus === 'number') return record.overallProdStatus
  const googleItem = normalizeChannelStatus(record, 'product')[0]
  if (googleItem.text === '有效') return 10
  if (googleItem.text === '失效') return 20
  return 0
}

export const getOverallPublishStatus = (record: goodsSetting.tableDataItem): number => {
  if (typeof record.publishStatus === 'number') return record.publishStatus
  if (typeof record.overallPublishStatus === 'number') return record.overallPublishStatus
  const items = normalizeChannelStatus(record, 'publish')
  if (items.some((item) => item.tone === 'error')) return 20
  if (items.length && items.every((item) => item.tone === 'success')) return 10
  return 0
}
